import React, {useState} from 'react';
import {Button, Confirm, Modal, Spinner} from "@tag/tag-components-react-v4";
import {CustomerDTO} from "../types/CustomerDTO";
import DataExportModal from "./DataExportModal";
import LockSiteModal from "./LockSiteModal";
import styles from './CustomerMenuModal.module.scss'
import AppRolesService from "../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import InternalAdminToolService from "../services/InternalAdminToolService";
import { ProjectAccents } from "../../General/config/projectAccents";

function CustomerMenuModal(props: Readonly<{
    selectedCustomer: CustomerDTO,
    handleClose: Function,
    setAlert: Function,
    reloadCustomerData: Function
}>) {

    const [dataExportVisible, setDataExportVisible] = useState(false);
    const [unlinkEvoConfirmVisible, setUnlinkEvoConfirmVisible] = useState(false);
    const [lockSiteVisible, setLockSiteVisible] = useState(false);
    const [loading, setLoading] = useState<string|boolean>(false)
    
    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    
    let isAdmin = AppRolesService.isUserAdmin(accounts, isAuthenticated);
    let canLockAccEnv = AppRolesService.canUserLockAccEnvironments(accounts, isAuthenticated);
    
    const handleUnlinkClientFromEvo = async () => {
        setLoading('Unlinking from Evo...');
        
        const success = await InternalAdminToolService.unlinkClientFromEvo(props.selectedCustomer.clientId, props.selectedCustomer.corpSite);
        
        if (success) {
            setLoading(false);
            props.setAlert({type: 'success', message: 'Successfully unlinked from Evo.'});
            props.handleClose();
        } else {
            setLoading(false);
            props.setAlert({type: 'error', message: 'Failed to unlink from Evo.'});
        }
    }
    
    let content;
    if (loading) {
        content = <Spinner full={true}>{loading}</Spinner>;
    }
    else {
        content =
            <Modal
                visible={!!props.selectedCustomer}
                accent={ProjectAccents.CareCompliance}
                heading={props.selectedCustomer?.name + ' - (' + props.selectedCustomer?.clientId + ')'}
                primaryButtonProps={{buttonStyle: {'display': 'none'}}}
                onCancelButtonClick={() => {
                    props.handleClose();
                }}
                onClose={() => props.handleClose()}
            >
                <div className={styles.CustomerMenuModalButtonContainer}>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Data Export'} onClick={() => setDataExportVisible(true)}/>
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Unlink from Evo'} disabled={!isAdmin} onClick={() => setUnlinkEvoConfirmVisible(true)}/>
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Lock Site for Termination'} disabled={!canLockAccEnv} onClick={() => setLockSiteVisible(true)}/>
                    </div>
                </div>
            </Modal>
    }

    return (
        <>
            {content}
            {dataExportVisible ?
                <DataExportModal
                    visible={dataExportVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setDataExportVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    setAlert={props.setAlert}
                /> : undefined
            }
            {unlinkEvoConfirmVisible ?
                <Confirm
                    visible={unlinkEvoConfirmVisible}
                    type="question"
                    heading="Continue"
                    onPrimaryButtonClick={() => handleUnlinkClientFromEvo()}
                    onCancelButtonClick={() => setUnlinkEvoConfirmVisible(false)}
                    onClose={() => setUnlinkEvoConfirmVisible(false)}
                >
                    Are you sure you want to unlink this organisation from evo?
                </Confirm> : undefined
            }
            {lockSiteVisible ?
                <LockSiteModal
                    visible={lockSiteVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setLockSiteVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    setAlert={props.setAlert}
                /> : undefined
            }
        </>
    )

}

export default CustomerMenuModal;
